import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Container,
    Grid,
    Group,
    Loader,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from "@mantine/core";

import { getAuthHeaders } from "../../../api/authHelper";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import Billede1 from "../../../Images/Billede1.jpg";

// ---------------------------------------------------------
// TYPES
// ---------------------------------------------------------

interface FormPracticeDto {
    practiceId?: number;
    country?: string;
    practiceDate?: string;

    practiceTime?: number;
    practiceLocation?: number;

    numberOfCourts?: number;
    numberOfPlayers?: number;
    numberOfMinuts?: number;

    createdDate?: string;
    eventId?: number;
    userId?: number;
}

interface OptionDto {
    value: number;
    text: string;
    eventid?: number;
}

interface EventDto {
    eventID: number;
    eventName: string;
    startTime?: string;
    endTime?: string;
}

interface GroupedPractice {
    key: string;

    date: string;

    timeValue?: number;
    timeText: string;

    locationValue?: number;
    locationText: string;

    bookings: FormPracticeDto[];

    totalCourts: number;
    totalPlayers: number;
}

interface CountrySummary {
    country: string;
    bookings: number;
    courts: number;
    players: number;
}

interface LocationSummary {
    locationValue?: number;
    location: string;
    bookings: number;
    courts: number;
    players: number;
}

// ---------------------------------------------------------
// HELPERS
// ---------------------------------------------------------

const normalizeDate = (date?: string) => {
    if (!date) return "";

    if (date.includes("T")) {
        return date.split("T")[0];
    }

    return date;
};

const formatDate = (date?: string) => {
    const normalized = normalizeDate(date);

    if (!normalized) {
        return "—";
    }

    const value = new Date(`${normalized}T00:00:00`);

    if (Number.isNaN(value.getTime())) {
        return normalized;
    }

    return value.toLocaleDateString("da-DK", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

// ---------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------

const FormPracticeAdminPage: React.FC = () => {
    const apiBase = process.env.REACT_APP_API_URL;

    // ---------------------------------------------------------
    // URLS
    // ---------------------------------------------------------

    const PRACTICE_URL = `${apiBase}/api/FormPractice`;

    const PRACTICE_TIME_URL =
        `${apiBase}/api/Option_PracticeTime`;

    const PRACTICE_MINUTES_URL =
        `${apiBase}/api/Option_PracticeMinuts`;

    const PRACTICE_LOCATION_URL =
        `${apiBase}/api/Option_PracticeLocation`;

    const EVENTS_URL =
        `${apiBase}/api/Events`;

    // ---------------------------------------------------------
    // STATE
    // ---------------------------------------------------------

    const [events, setEvents] = useState<EventDto[]>([]);

    const [selectedEventId, setSelectedEventId] =
        useState<string | null>(null);

    const [practices, setPractices] =
        useState<FormPracticeDto[]>([]);

    const [practiceTimes, setPracticeTimes] =
        useState<OptionDto[]>([]);

    const [practiceMinutes, setPracticeMinutes] =
        useState<OptionDto[]>([]);

    const [practiceLocations, setPracticeLocations] =
        useState<OptionDto[]>([]);

    const [loadingEvents, setLoadingEvents] =
        useState(false);

    const [loadingPractices, setLoadingPractices] =
        useState(false);

    const [error, setError] =
        useState<string | null>(null);

    // ---------------------------------------------------------
    // FILTERS
    // ---------------------------------------------------------

    const [countryFilter, setCountryFilter] =
        useState("");

    const [dateFilter, setDateFilter] =
        useState<string | null>(null);

    const [timeFilter, setTimeFilter] =
        useState<string | null>(null);

    const [locationFilter, setLocationFilter] =
        useState<string | null>(null);

    // ---------------------------------------------------------
    // OPTION HELPERS
    // ---------------------------------------------------------

    const getPracticeTimeText = (value?: number) => {
        if (value === undefined || value === null) {
            return "—";
        }

        return (
            practiceTimes.find(
                (x) => x.value === value
            )?.text ?? String(value)
        );
    };

    const getPracticeMinutesText = (value?: number) => {
        if (value === undefined || value === null) {
            return "—";
        }

        return (
            practiceMinutes.find(
                (x) => x.value === value
            )?.text ?? `${value} min.`
        );
    };

    const getPracticeLocationText = (
        value?: number
    ) => {
        if (value === undefined || value === null) {
            return "—";
        }

        return (
            practiceLocations.find(
                (x) => x.value === value
            )?.text ?? String(value)
        );
    };

    // ---------------------------------------------------------
    // LOAD EVENTS
    // ---------------------------------------------------------

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoadingEvents(true);
            setError(null);

            const res = await fetch(EVENTS_URL, {
                headers:
                    getAuthHeaders() as Record<
                        string,
                        string
                    >,
            });

            if (!res.ok) {
                throw new Error(
                    `Kunne ikke hente events (${res.status})`
                );
            }

            const data = await res.json();

            const eventList: EventDto[] =
                Array.isArray(data)
                    ? data
                    : data.items ??
                    data.events ??
                    [];

            setEvents(eventList);

            if (eventList.length > 0) {
                setSelectedEventId(
                    String(eventList[0].eventID)
                );
            }
        } catch (err) {
            console.error(err);

            setError(
                "Kunne ikke hente events."
            );
        } finally {
            setLoadingEvents(false);
        }
    };

    // ---------------------------------------------------------
    // EVENT CHANGE
    // ---------------------------------------------------------

    useEffect(() => {
        if (!selectedEventId) {
            setPractices([]);
            setPracticeTimes([]);
            setPracticeMinutes([]);
            setPracticeLocations([]);

            return;
        }

        const eventId =
            Number(selectedEventId);

        resetFilters();

        loadEventData(eventId);
    }, [selectedEventId]);

    // ---------------------------------------------------------
    // LOAD ALL EVENT DATA
    // ---------------------------------------------------------

    const loadEventData = async (
        eventId: number
    ) => {
        try {
            setLoadingPractices(true);
            setError(null);

            await Promise.all([
                fetchPractices(eventId),
                fetchPracticeTimes(eventId),
                fetchPracticeMinutes(eventId),
                fetchPracticeLocations(eventId),
            ]);
        } catch (err) {
            console.error(err);

            setError(
                "Der opstod en fejl ved indlæsning af træningsdata."
            );
        } finally {
            setLoadingPractices(false);
        }
    };

    // ---------------------------------------------------------
    // FETCH PRACTICES
    // ---------------------------------------------------------

    const fetchPractices = async (
        eventId: number
    ) => {
        const res = await fetch(
            `${PRACTICE_URL}/admin?eventId=${eventId}`,
            {
                headers:
                    getAuthHeaders() as Record<
                        string,
                        string
                    >,
            }
        );

        if (!res.ok) {
            throw new Error(
                `Kunne ikke hente træningsbookinger (${res.status})`
            );
        }

        const data: FormPracticeDto[] =
            await res.json();

        setPractices(
            Array.isArray(data) ? data : []
        );
    };

    // ---------------------------------------------------------
    // FETCH TIMES
    // ---------------------------------------------------------

    const fetchPracticeTimes = async (
        eventId: number
    ) => {
        const res = await fetch(
            `${PRACTICE_TIME_URL}?eventId=${eventId}`,
            {
                headers:
                    getAuthHeaders() as Record<
                        string,
                        string
                    >,
            }
        );

        if (!res.ok) {
            throw new Error(
                `Kunne ikke hente træningstider (${res.status})`
            );
        }

        const data = await res.json();

        setPracticeTimes(
            Array.isArray(data) ? data : []
        );
    };

    // ---------------------------------------------------------
    // FETCH MINUTES
    // ---------------------------------------------------------

    const fetchPracticeMinutes = async (
        eventId: number
    ) => {
        const res = await fetch(
            `${PRACTICE_MINUTES_URL}?eventId=${eventId}`,
            {
                headers:
                    getAuthHeaders() as Record<
                        string,
                        string
                    >,
            }
        );

        if (!res.ok) {
            throw new Error(
                `Kunne ikke hente varigheder (${res.status})`
            );
        }

        const data = await res.json();

        setPracticeMinutes(
            Array.isArray(data) ? data : []
        );
    };

    // ---------------------------------------------------------
    // FETCH LOCATIONS
    // ---------------------------------------------------------

    const fetchPracticeLocations = async (
        eventId: number
    ) => {
        const res = await fetch(
            `${PRACTICE_LOCATION_URL}?eventId=${eventId}`,
            {
                headers:
                    getAuthHeaders() as Record<
                        string,
                        string
                    >,
            }
        );

        if (!res.ok) {
            throw new Error(
                `Kunne ikke hente træningssteder (${res.status})`
            );
        }

        const data = await res.json();

        setPracticeLocations(
            Array.isArray(data) ? data : []
        );
    };

    // ---------------------------------------------------------
    // REFRESH
    // ---------------------------------------------------------

    const refreshData = async () => {
        if (!selectedEventId) {
            return;
        }

        await loadEventData(
            Number(selectedEventId)
        );
    };

    // ---------------------------------------------------------
    // AVAILABLE DATES
    // ---------------------------------------------------------

    const availableDates = useMemo(() => {
        const dates = Array.from(
            new Set(
                practices
                    .map((x) =>
                        normalizeDate(
                            x.practiceDate
                        )
                    )
                    .filter(Boolean)
            )
        );

        return dates
            .sort()
            .map((date) => ({
                value: date,
                label: formatDate(date),
            }));
    }, [practices]);

    // ---------------------------------------------------------
    // FILTERED PRACTICES
    // ---------------------------------------------------------

    const filteredPractices =
        useMemo(() => {
            const countrySearch =
                countryFilter
                    .trim()
                    .toLowerCase();

            return practices
                .filter((practice) => {
                    // COUNTRY
                    if (
                        countrySearch &&
                        !practice.country
                            ?.toLowerCase()
                            .includes(
                                countrySearch
                            )
                    ) {
                        return false;
                    }

                    // DATE
                    if (
                        dateFilter &&
                        normalizeDate(
                            practice.practiceDate
                        ) !== dateFilter
                    ) {
                        return false;
                    }

                    // TIME
                    if (
                        timeFilter &&
                        String(
                            practice.practiceTime
                        ) !== timeFilter
                    ) {
                        return false;
                    }

                    // LOCATION
                    if (
                        locationFilter &&
                        String(
                            practice.practiceLocation
                        ) !== locationFilter
                    ) {
                        return false;
                    }

                    return true;
                })
                .sort((a, b) => {
                    const dateA =
                        normalizeDate(
                            a.practiceDate
                        );

                    const dateB =
                        normalizeDate(
                            b.practiceDate
                        );

                    if (dateA !== dateB) {
                        return dateA.localeCompare(
                            dateB
                        );
                    }

                    if (
                        a.practiceTime !==
                        b.practiceTime
                    ) {
                        return (
                            (a.practiceTime ?? 0) -
                            (b.practiceTime ?? 0)
                        );
                    }

                    return (
                        (a.practiceLocation ??
                            0) -
                        (b.practiceLocation ??
                            0)
                    );
                });
        }, [
            practices,
            countryFilter,
            dateFilter,
            timeFilter,
            locationFilter,
        ]);

    // ---------------------------------------------------------
    // TOTALS
    // ---------------------------------------------------------

    const totals = useMemo(() => {
        return filteredPractices.reduce(
            (acc, practice) => {
                acc.bookings += 1;

                acc.courts +=
                    practice.numberOfCourts ??
                    0;

                acc.players +=
                    practice.numberOfPlayers ??
                    0;

                return acc;
            },
            {
                bookings: 0,
                courts: 0,
                players: 0,
            }
        );
    }, [filteredPractices]);

    // ---------------------------------------------------------
    // GROUP BY DATE + TIME + LOCATION
    // ---------------------------------------------------------

    const groupedPractices =
        useMemo(() => {
            const map = new Map<
                string,
                GroupedPractice
            >();

            filteredPractices.forEach(
                (practice) => {
                    const date =
                        normalizeDate(
                            practice.practiceDate
                        );

                    const time =
                        practice.practiceTime;

                    const location =
                        practice.practiceLocation;

                    const key =
                        `${date}-${time ?? "none"}-${location ?? "none"}`;

                    if (!map.has(key)) {
                        map.set(key, {
                            key,

                            date,

                            timeValue: time,

                            timeText:
                                getPracticeTimeText(
                                    time
                                ),

                            locationValue:
                                location,

                            locationText:
                                getPracticeLocationText(
                                    location
                                ),

                            bookings: [],

                            totalCourts: 0,

                            totalPlayers: 0,
                        });
                    }

                    const group =
                        map.get(key)!;

                    group.bookings.push(
                        practice
                    );

                    group.totalCourts +=
                        practice.numberOfCourts ??
                        0;

                    group.totalPlayers +=
                        practice.numberOfPlayers ??
                        0;
                }
            );

            return Array.from(
                map.values()
            ).sort((a, b) => {
                if (a.date !== b.date) {
                    return a.date.localeCompare(
                        b.date
                    );
                }

                if (
                    a.timeValue !==
                    b.timeValue
                ) {
                    return (
                        (a.timeValue ?? 0) -
                        (b.timeValue ?? 0)
                    );
                }

                return a.locationText.localeCompare(
                    b.locationText
                );
            });
        }, [
            filteredPractices,
            practiceTimes,
            practiceLocations,
        ]);

    // ---------------------------------------------------------
    // COUNTRY SUMMARY
    // ---------------------------------------------------------

    const countrySummary =
        useMemo<CountrySummary[]>(() => {
            const map =
                new Map<
                    string,
                    CountrySummary
                >();

            filteredPractices.forEach(
                (practice) => {
                    const country =
                        practice.country?.trim() ||
                        "Ukendt";

                    if (!map.has(country)) {
                        map.set(country, {
                            country,
                            bookings: 0,
                            courts: 0,
                            players: 0,
                        });
                    }

                    const item =
                        map.get(country)!;

                    item.bookings += 1;

                    item.courts +=
                        practice.numberOfCourts ??
                        0;

                    item.players +=
                        practice.numberOfPlayers ??
                        0;
                }
            );

            return Array.from(
                map.values()
            ).sort(
                (a, b) =>
                    b.courts - a.courts
            );
        }, [filteredPractices]);

    // ---------------------------------------------------------
    // LOCATION SUMMARY
    // ---------------------------------------------------------

    const locationSummary =
        useMemo<LocationSummary[]>(() => {
            const map =
                new Map<
                    string,
                    LocationSummary
                >();

            filteredPractices.forEach(
                (practice) => {
                    const locationValue =
                        practice.practiceLocation;

                    const key =
                        locationValue !==
                            undefined &&
                            locationValue !== null
                            ? String(
                                locationValue
                            )
                            : "none";

                    if (!map.has(key)) {
                        map.set(key, {
                            locationValue,

                            location:
                                getPracticeLocationText(
                                    locationValue
                                ),

                            bookings: 0,

                            courts: 0,

                            players: 0,
                        });
                    }

                    const item =
                        map.get(key)!;

                    item.bookings += 1;

                    item.courts +=
                        practice.numberOfCourts ??
                        0;

                    item.players +=
                        practice.numberOfPlayers ??
                        0;
                }
            );

            return Array.from(
                map.values()
            ).sort(
                (a, b) =>
                    b.courts - a.courts
            );
        }, [
            filteredPractices,
            practiceLocations,
        ]);

    // ---------------------------------------------------------
    // RESET FILTERS
    // ---------------------------------------------------------

    const resetFilters = () => {
        setCountryFilter("");
        setDateFilter(null);
        setTimeFilter(null);
        setLocationFilter(null);
    };

    // ---------------------------------------------------------
    // CURRENT EVENT
    // ---------------------------------------------------------

    const selectedEvent =
        useMemo(() => {
            if (!selectedEventId) {
                return undefined;
            }

            return events.find(
                (event) =>
                    String(event.eventID) ===
                    selectedEventId
            );
        }, [
            events,
            selectedEventId,
        ]);

    // ---------------------------------------------------------
    // RENDER
    // ---------------------------------------------------------

    return (
        <div>
            <HeroSectionV2
                titleEn="Training plan"
                titleDa="Træningsplan"
                descriptionEn="See practice requests"
                descriptionDa="Se træningsønsker"
                imageSrc={Billede1}
            />

            <Container
                size="xl"
                py="xl"
            >
                <Stack gap="lg">
                    {/* TITLE */}

                    <Group
                        justify="space-between"
                        align="flex-end"
                    >
                        <div>
                            <Title order={1}>
                                Træningsbookinger
                            </Title>

                            <Text c="dimmed">
                                Administrativt
                                overblik over ønsker
                                til træningsbaner.
                            </Text>
                        </div>

                        <Button
                            variant="default"
                            onClick={
                                refreshData
                            }
                            loading={
                                loadingPractices
                            }
                            disabled={
                                !selectedEventId
                            }
                        >
                            Genindlæs
                        </Button>
                    </Group>

                    {/* ERROR */}

                    {error && (
                        <Alert
                            color="red"
                            title="Fejl"
                        >
                            {error}
                        </Alert>
                    )}

                    {/* EVENT */}

                    <Paper
                        withBorder
                        p="lg"
                        radius="md"
                    >
                        <Group
                            align="end"
                            justify="space-between"
                        >
                            <Group align="end">
                                <Select
                                    label="Event"
                                    placeholder={
                                        loadingEvents
                                            ? "Henter events..."
                                            : "Vælg event"
                                    }
                                    searchable
                                    clearable={
                                        false
                                    }
                                    value={
                                        selectedEventId
                                    }
                                    onChange={
                                        setSelectedEventId
                                    }
                                    data={events.map(
                                        (
                                            event
                                        ) => ({
                                            value:
                                                String(
                                                    event.eventID
                                                ),
                                            label:
                                                event.eventName,
                                        })
                                    )}
                                    disabled={
                                        loadingEvents
                                    }
                                    style={{
                                        minWidth:
                                            320,
                                    }}
                                />

                                {loadingEvents && (
                                    <Loader size="sm" />
                                )}
                            </Group>

                            {selectedEvent && (
                                <Text
                                    size="sm"
                                    c="dimmed"
                                >
                                    Event ID:{" "}
                                    {
                                        selectedEvent.eventID
                                    }
                                </Text>
                            )}
                        </Group>
                    </Paper>

                    {/* FILTERS */}

                    <Paper
                        withBorder
                        p="lg"
                        radius="md"
                    >
                        <Stack gap="md">
                            <Group
                                justify="space-between"
                                align="flex-start"
                            >
                                <div>
                                    <Title order={3}>
                                        Filtre
                                    </Title>

                                    <Text
                                        size="sm"
                                        c="dimmed"
                                    >
                                        Filtrér på
                                        land, dato,
                                        tidspunkt
                                        eller
                                        træningssted.
                                    </Text>
                                </div>

                                <Button
                                    variant="default"
                                    onClick={
                                        resetFilters
                                    }
                                >
                                    Nulstil filtre
                                </Button>
                            </Group>

                            <Grid>
                                <Grid.Col
                                    span={{
                                        base: 12,
                                        sm: 6,
                                        lg: 3,
                                    }}
                                >
                                    <TextInput
                                        label="Land"
                                        placeholder="Fx Denmark, China..."
                                        value={
                                            countryFilter
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setCountryFilter(
                                                event
                                                    .currentTarget
                                                    .value
                                            )
                                        }
                                    />
                                </Grid.Col>

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        sm: 6,
                                        lg: 3,
                                    }}
                                >
                                    <Select
                                        label="Dato"
                                        placeholder="Alle datoer"
                                        clearable
                                        searchable
                                        value={
                                            dateFilter
                                        }
                                        onChange={
                                            setDateFilter
                                        }
                                        data={
                                            availableDates
                                        }
                                    />
                                </Grid.Col>

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        sm: 6,
                                        lg: 3,
                                    }}
                                >
                                    <Select
                                        label="Tidspunkt"
                                        placeholder="Alle tidspunkter"
                                        clearable
                                        value={
                                            timeFilter
                                        }
                                        onChange={
                                            setTimeFilter
                                        }
                                        data={practiceTimes.map(
                                            (
                                                time
                                            ) => ({
                                                value:
                                                    String(
                                                        time.value
                                                    ),
                                                label:
                                                    time.text,
                                            })
                                        )}
                                    />
                                </Grid.Col>

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        sm: 6,
                                        lg: 3,
                                    }}
                                >
                                    <Select
                                        label="Træningssted"
                                        placeholder="Alle steder"
                                        clearable
                                        searchable
                                        value={
                                            locationFilter
                                        }
                                        onChange={
                                            setLocationFilter
                                        }
                                        data={practiceLocations.map(
                                            (
                                                location
                                            ) => ({
                                                value:
                                                    String(
                                                        location.value
                                                    ),
                                                label:
                                                    location.text,
                                            })
                                        )}
                                    />
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Paper>

                    {/* LOADING */}

                    {loadingPractices ? (
                        <Paper
                            withBorder
                            p={50}
                            radius="md"
                        >
                            <Group justify="center">
                                <Loader />

                                <Text>
                                    Henter
                                    træningsbookinger...
                                </Text>
                            </Group>
                        </Paper>
                    ) : practices.length ===
                        0 ? (
                        <Paper
                            withBorder
                            p={50}
                            radius="md"
                        >
                            <Stack
                                align="center"
                                gap="xs"
                            >
                                <Title order={3}>
                                    Ingen
                                    træningsbookinger
                                </Title>

                                <Text c="dimmed">
                                    Der er endnu
                                    ikke oprettet
                                    nogen ønsker
                                    til dette event.
                                </Text>
                            </Stack>
                        </Paper>
                    ) : (
                        <>
                            {/* KPI */}

                            <SimpleGrid
                                cols={{
                                    base: 2,
                                    md: 4,
                                }}
                                spacing="md"
                            >
                                <Card
                                    withBorder
                                    radius="md"
                                    padding="lg"
                                >
                                    <Text
                                        size="sm"
                                        c="dimmed"
                                    >
                                        Bookingønsker
                                    </Text>

                                    <Text
                                        size="2rem"
                                        fw={700}
                                    >
                                        {
                                            totals.bookings
                                        }
                                    </Text>
                                </Card>

                                <Card
                                    withBorder
                                    radius="md"
                                    padding="lg"
                                >
                                    <Text
                                        size="sm"
                                        c="dimmed"
                                    >
                                        Ønskede baner
                                    </Text>

                                    <Text
                                        size="2rem"
                                        fw={700}
                                    >
                                        {
                                            totals.courts
                                        }
                                    </Text>
                                </Card>

                                <Card
                                    withBorder
                                    radius="md"
                                    padding="lg"
                                >
                                    <Text
                                        size="sm"
                                        c="dimmed"
                                    >
                                        Spillere
                                    </Text>

                                    <Text
                                        size="2rem"
                                        fw={700}
                                    >
                                        {
                                            totals.players
                                        }
                                    </Text>
                                </Card>

                                <Card
                                    withBorder
                                    radius="md"
                                    padding="lg"
                                >
                                    <Text
                                        size="sm"
                                        c="dimmed"
                                    >
                                        Lande
                                    </Text>

                                    <Text
                                        size="2rem"
                                        fw={700}
                                    >
                                        {
                                            countrySummary.length
                                        }
                                    </Text>
                                </Card>
                            </SimpleGrid>

                            {/* GROUPED */}

                            <Paper
                                withBorder
                                p="lg"
                                radius="md"
                            >
                                <Stack gap="md">
                                    <div>
                                        <Title
                                            order={
                                                2
                                            }
                                        >
                                            Overblik
                                            pr.
                                            tidspunkt
                                            og hal
                                        </Title>

                                        <Text
                                            size="sm"
                                            c="dimmed"
                                        >
                                            Samlet
                                            efterspørgsel
                                            fordelt på
                                            dato,
                                            tidspunkt
                                            og
                                            træningssted.
                                        </Text>
                                    </div>

                                    {groupedPractices.length ===
                                        0 ? (
                                        <Text c="dimmed">
                                            Ingen
                                            bookinger
                                            matcher de
                                            valgte
                                            filtre.
                                        </Text>
                                    ) : (
                                        <SimpleGrid
                                            cols={{
                                                base: 1,
                                                md: 2,
                                                xl: 3,
                                            }}
                                        >
                                            {groupedPractices.map(
                                                (
                                                    group
                                                ) => (
                                                    <Card
                                                        key={
                                                            group.key
                                                        }
                                                        withBorder
                                                        radius="md"
                                                        padding="lg"
                                                    >
                                                        <Stack gap="sm">
                                                            <Group
                                                                justify="space-between"
                                                                align="flex-start"
                                                            >
                                                                <div>
                                                                    <Text
                                                                        fw={
                                                                            700
                                                                        }
                                                                        size="lg"
                                                                    >
                                                                        {formatDate(
                                                                            group.date
                                                                        )}
                                                                    </Text>

                                                                    <Text
                                                                        fw={
                                                                            600
                                                                        }
                                                                        size="xl"
                                                                    >
                                                                        {
                                                                            group.timeText
                                                                        }
                                                                    </Text>

                                                                    <Text
                                                                        size="sm"
                                                                        c="dimmed"
                                                                        fw={
                                                                            500
                                                                        }
                                                                    >
                                                                        {
                                                                            group.locationText
                                                                        }
                                                                    </Text>
                                                                </div>

                                                                <Badge
                                                                    size="lg"
                                                                    variant="light"
                                                                >
                                                                    {
                                                                        group
                                                                            .bookings
                                                                            .length
                                                                    }{" "}
                                                                    ønsker
                                                                </Badge>
                                                            </Group>

                                                            <Group gap="xl">
                                                                <div>
                                                                    <Text
                                                                        size="xs"
                                                                        c="dimmed"
                                                                    >
                                                                        Baner
                                                                    </Text>

                                                                    <Text
                                                                        fw={
                                                                            700
                                                                        }
                                                                        size="xl"
                                                                    >
                                                                        {
                                                                            group.totalCourts
                                                                        }
                                                                    </Text>
                                                                </div>

                                                                <div>
                                                                    <Text
                                                                        size="xs"
                                                                        c="dimmed"
                                                                    >
                                                                        Spillere
                                                                    </Text>

                                                                    <Text
                                                                        fw={
                                                                            700
                                                                        }
                                                                        size="xl"
                                                                    >
                                                                        {
                                                                            group.totalPlayers
                                                                        }
                                                                    </Text>
                                                                </div>
                                                            </Group>

                                                            <div>
                                                                {group.bookings.map(
                                                                    (
                                                                        booking
                                                                    ) => (
                                                                        <Group
                                                                            key={
                                                                                booking.practiceId
                                                                            }
                                                                            justify="space-between"
                                                                            style={{
                                                                                borderTop:
                                                                                    "1px solid #eee",
                                                                                paddingTop: 8,
                                                                                marginTop: 8,
                                                                            }}
                                                                        >
                                                                            <Text
                                                                                size="sm"
                                                                                fw={
                                                                                    500
                                                                                }
                                                                            >
                                                                                {booking.country ||
                                                                                    "—"}
                                                                            </Text>

                                                                            <Text
                                                                                size="sm"
                                                                                c="dimmed"
                                                                            >
                                                                                {booking.numberOfCourts ??
                                                                                    0}{" "}
                                                                                baner
                                                                                ·{" "}
                                                                                {booking.numberOfPlayers ??
                                                                                    0}{" "}
                                                                                spillere
                                                                            </Text>
                                                                        </Group>
                                                                    )
                                                                )}
                                                            </div>
                                                        </Stack>
                                                    </Card>
                                                )
                                            )}
                                        </SimpleGrid>
                                    )}
                                </Stack>
                            </Paper>

                            {/* LOCATION + COUNTRY */}

                            <Grid>
                                {/* LOCATION SUMMARY */}

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        lg: 6,
                                    }}
                                >
                                    <Paper
                                        withBorder
                                        p="lg"
                                        radius="md"
                                        h="100%"
                                    >
                                        <Stack gap="md">
                                            <div>
                                                <Title
                                                    order={
                                                        2
                                                    }
                                                >
                                                    Overblik
                                                    pr.
                                                    træningssted
                                                </Title>

                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Samlet
                                                    efterspørgsel
                                                    på de
                                                    enkelte
                                                    haller.
                                                </Text>
                                            </div>

                                            <Table
                                                striped
                                                highlightOnHover
                                            >
                                                <Table.Thead>
                                                    <Table.Tr>
                                                        <Table.Th>
                                                            Sted
                                                        </Table.Th>

                                                        <Table.Th>
                                                            Ønsker
                                                        </Table.Th>

                                                        <Table.Th>
                                                            Baner
                                                        </Table.Th>

                                                        <Table.Th>
                                                            Spillere
                                                        </Table.Th>
                                                    </Table.Tr>
                                                </Table.Thead>

                                                <Table.Tbody>
                                                    {locationSummary.map(
                                                        (
                                                            item
                                                        ) => (
                                                            <Table.Tr
                                                                key={
                                                                    item.locationValue ??
                                                                    "none"
                                                                }
                                                            >
                                                                <Table.Td>
                                                                    <Text
                                                                        fw={
                                                                            600
                                                                        }
                                                                    >
                                                                        {
                                                                            item.location
                                                                        }
                                                                    </Text>
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    {
                                                                        item.bookings
                                                                    }
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    {
                                                                        item.courts
                                                                    }
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    {
                                                                        item.players
                                                                    }
                                                                </Table.Td>
                                                            </Table.Tr>
                                                        )
                                                    )}
                                                </Table.Tbody>
                                            </Table>
                                        </Stack>
                                    </Paper>
                                </Grid.Col>

                                {/* COUNTRY SUMMARY */}

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        lg: 6,
                                    }}
                                >
                                    <Paper
                                        withBorder
                                        p="lg"
                                        radius="md"
                                        h="100%"
                                    >
                                        <Stack gap="md">
                                            <div>
                                                <Title
                                                    order={
                                                        2
                                                    }
                                                >
                                                    Overblik
                                                    pr.
                                                    land
                                                </Title>

                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Samlet
                                                    efterspørgsel
                                                    fra hvert
                                                    land.
                                                </Text>
                                            </div>

                                            <Table
                                                striped
                                                highlightOnHover
                                            >
                                                <Table.Thead>
                                                    <Table.Tr>
                                                        <Table.Th>
                                                            Land
                                                        </Table.Th>

                                                        <Table.Th>
                                                            Ønsker
                                                        </Table.Th>

                                                        <Table.Th>
                                                            Baner
                                                        </Table.Th>

                                                        <Table.Th>
                                                            Spillere
                                                        </Table.Th>
                                                    </Table.Tr>
                                                </Table.Thead>

                                                <Table.Tbody>
                                                    {countrySummary.map(
                                                        (
                                                            item
                                                        ) => (
                                                            <Table.Tr
                                                                key={
                                                                    item.country
                                                                }
                                                            >
                                                                <Table.Td>
                                                                    <Text
                                                                        fw={
                                                                            600
                                                                        }
                                                                    >
                                                                        {
                                                                            item.country
                                                                        }
                                                                    </Text>
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    {
                                                                        item.bookings
                                                                    }
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    {
                                                                        item.courts
                                                                    }
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    {
                                                                        item.players
                                                                    }
                                                                </Table.Td>
                                                            </Table.Tr>
                                                        )
                                                    )}
                                                </Table.Tbody>
                                            </Table>
                                        </Stack>
                                    </Paper>
                                </Grid.Col>
                            </Grid>

                            {/* DETAILS */}

                            <Paper
                                withBorder
                                p="lg"
                                radius="md"
                            >
                                <Stack gap="md">
                                    <div>
                                        <Title
                                            order={2}
                                        >
                                            Alle
                                            bookingønsker
                                        </Title>

                                        <Text
                                            size="sm"
                                            c="dimmed"
                                        >
                                            {
                                                filteredPractices.length
                                            }{" "}
                                            af{" "}
                                            {
                                                practices.length
                                            }{" "}
                                            bookinger
                                            vises.
                                        </Text>
                                    </div>

                                    <div
                                        style={{
                                            overflowX:
                                                "auto",
                                        }}
                                    >
                                        <Table
                                            striped
                                            highlightOnHover
                                            withTableBorder
                                        >
                                            <Table.Thead>
                                                <Table.Tr>
                                                    <Table.Th>
                                                        Land
                                                    </Table.Th>

                                                    <Table.Th>
                                                        Dato
                                                    </Table.Th>

                                                    <Table.Th>
                                                        Tid
                                                    </Table.Th>

                                                    <Table.Th>
                                                        Sted
                                                    </Table.Th>

                                                    <Table.Th>
                                                        Baner
                                                    </Table.Th>

                                                    <Table.Th>
                                                        Spillere
                                                    </Table.Th>

                                                    <Table.Th>
                                                        Varighed
                                                    </Table.Th>

                                                    <Table.Th>
                                                        User
                                                        ID
                                                    </Table.Th>

                                                    <Table.Th>
                                                        Oprettet
                                                    </Table.Th>
                                                </Table.Tr>
                                            </Table.Thead>

                                            <Table.Tbody>
                                                {filteredPractices.map(
                                                    (
                                                        practice
                                                    ) => (
                                                        <Table.Tr
                                                            key={
                                                                practice.practiceId
                                                            }
                                                        >
                                                            <Table.Td>
                                                                <Text
                                                                    fw={
                                                                        600
                                                                    }
                                                                >
                                                                    {practice.country ||
                                                                        "—"}
                                                                </Text>
                                                            </Table.Td>

                                                            <Table.Td>
                                                                {formatDate(
                                                                    practice.practiceDate
                                                                )}
                                                            </Table.Td>

                                                            <Table.Td>
                                                                <Badge variant="light">
                                                                    {getPracticeTimeText(
                                                                        practice.practiceTime
                                                                    )}
                                                                </Badge>
                                                            </Table.Td>

                                                            <Table.Td>
                                                                {practice.practiceLocation !==
                                                                    undefined &&
                                                                    practice.practiceLocation !==
                                                                    null ? (
                                                                    <Badge
                                                                        variant="outline"
                                                                    >
                                                                        {getPracticeLocationText(
                                                                            practice.practiceLocation
                                                                        )}
                                                                    </Badge>
                                                                ) : (
                                                                    "—"
                                                                )}
                                                            </Table.Td>

                                                            <Table.Td>
                                                                {practice.numberOfCourts ??
                                                                    "—"}
                                                            </Table.Td>

                                                            <Table.Td>
                                                                {practice.numberOfPlayers ??
                                                                    "—"}
                                                            </Table.Td>

                                                            <Table.Td>
                                                                {getPracticeMinutesText(
                                                                    practice.numberOfMinuts
                                                                )}
                                                            </Table.Td>

                                                            <Table.Td>
                                                                {practice.userId ??
                                                                    "—"}
                                                            </Table.Td>

                                                            <Table.Td>
                                                                {practice.createdDate
                                                                    ? new Date(
                                                                        practice.createdDate
                                                                    ).toLocaleString(
                                                                        "da-DK"
                                                                    )
                                                                    : "—"}
                                                            </Table.Td>
                                                        </Table.Tr>
                                                    )
                                                )}
                                            </Table.Tbody>
                                        </Table>
                                    </div>
                                </Stack>
                            </Paper>
                        </>
                    )}
                </Stack>
            </Container>
        </div>
    );
};

export default FormPracticeAdminPage;